import Initializer from "./Components/Blocks/Initializer";
import Waypoint from "./Utils/Waypoint";
import DelayedClassAssignment from "./Utils/DelayedClassAssignment";
import SiteHeader from "./Components/SiteHeader";
import FullscreenElement from "./Components/FullscreenElement";
import MiscComponentInitializer from "./Utils/MiscComponentInitializer";
import MobileMenu from "./Components/MobileMenu";
import SiteFooter from "./Components/SiteFooter";
import FsLightboxInitilaizer from "./Utils/FsLightboxInitializer";
import GoogleMap from "./Components/GoogleMap";
import AutoExpandTextarea from "./Components/AutoExpandTextarea";
import AdaCompliance from "./Components/AdaCompliance";
import Blog from "./Components/Blog";
import BusinessHours from "./Components/BusinessHours";
import SearchAutoComplete from "./Components/SearchAutoComplete";
import ParallaxScrollingElementCollection from "./Components/ParallaxScrollingElementCollection";
import PostLoaderCollection from "./Components/PostLoaderCollection";
import DefaultHero from "./Components/DefaultHero";
import WindowScrollObserver from "./Utils/WindowScrollObserver";
import GravityForms from "@/ts/base/Components/GravityForms";
import ThemeOption from "@/ts/base/Interfaces/ThemeOption";


class Main {
	private static instance: Main;

	protected header!: SiteHeader | null;
	protected footer!: SiteFooter | null;
	protected mobileMenu!: MobileMenu | null;
	protected miscCompInitializer!: MiscComponentInitializer | null;

	options!: ThemeOption;

	postLoaders!: PostLoaderCollection | null;

	isLoaded = false;
	scrollObserver = new WindowScrollObserver();

	/**
	 * @return {Main}
	 * @constructor
	 */
	static get Instance(): Main {
		return Main.instance;
	}

	static get IsLoadedClass(): string {
		return "theme-loaded";
	}

	constructor() {
		Main.instance = this;

		document.addEventListener("DOMContentLoaded", this.init.bind(this));
		document.fonts.addEventListener("loadingdone", this.onFontsLoaded.bind(this));
	}

	getHeaderComponent(): SiteHeader | null {
		return this.header;
	}

	getFooterComponent(): SiteFooter | null {
		return this.footer;
	}

	getMobileMenuComponent(): MobileMenu | null {
		return this.mobileMenu;
	}

	init() {
		document.documentElement.dataset.agent = navigator.userAgent;

		this.header = this.initializeSiteHeader();
		this.footer = this.initializeSiteFooter();
		this.mobileMenu = this.initializeMobileMenu();
		this.miscCompInitializer = this.initializeMiscComponents();
		this.postLoaders = this.initializePostLoaderCollection();

		this.initializeFullscreenElements();
		this.initializeFsLightbox();
		this.initializeAutoExandTextAreas();
		this.initializeGravityForms();
		this.initializeGoogleMaps();
		this.initializeAdaCompliance();
		this.initializeBusinessHours();
		this.initializeSearchAutoComplete();
		this.initializeParallaxScrollingElementsCollection();
		this.initializeDefaultHero();

		this.initializeDelayedClassAssigment();
		this.initializeWaypoints();
		this.initializeBlocks();
		this.initializeBlog();

		this.isLoaded = true;
	}

	initializeSiteHeader(): SiteHeader | null {
		return new SiteHeader();
	}

	initializeSiteFooter(): SiteFooter | null {
		return new SiteFooter();
	}

	initializeMobileMenu(): MobileMenu | null {
		return new MobileMenu();
	}

	initializeDelayedClassAssigment(): DelayedClassAssignment | null {
		return new DelayedClassAssignment();
	}

	initializeWaypoints(): Waypoint | null {
		return new Waypoint(".waypoint");
	}

	initializeFullscreenElements(): FullscreenElement | null {
		return new FullscreenElement(".fullscreen");
	}

	initializeGravityForms(): GravityForms | null {
		return new GravityForms();
	}

	initializeFsLightbox(): FsLightboxInitilaizer | null {
		return new FsLightboxInitilaizer("a.popup");
	}

	initializeAutoExandTextAreas(): AutoExpandTextarea | null {
		return new AutoExpandTextarea("textarea");
	}

	initializeBlog(): Blog | null {
		return new Blog();
	}

	initializeGoogleMaps(): void {
		document.querySelectorAll(".acf-map").forEach((mapEl) => {
			new GoogleMap(<HTMLElement>mapEl);
		});
	}

	initializeBlocks(): Initializer {
		return new Initializer();
	}

	initializeMiscComponents(): MiscComponentInitializer | null {
		return new MiscComponentInitializer();
	}

	initializeAdaCompliance(): AdaCompliance | null {
		return new AdaCompliance();
	}

	initializeBusinessHours(): BusinessHours | null {
		return new BusinessHours();
	}

	initializeSearchAutoComplete(): SearchAutoComplete | null {
		return new SearchAutoComplete("input[type=search]", document.querySelector(".content-sidebar form[role=search]") as HTMLFormElement);
	}

	initializeParallaxScrollingElementsCollection(): ParallaxScrollingElementCollection | null {
		return new ParallaxScrollingElementCollection(".parallax-container");
	}

	initializePostLoaderCollection(): PostLoaderCollection | null {
		return new PostLoaderCollection();
	}

	initializeDefaultHero(): DefaultHero | null {
		return new DefaultHero(window.document.querySelector(".section.default-hero"));
	}

	protected onFontsLoaded(e: Event): void {
		document.body.classList.add(Main.IsLoadedClass);
		window.dispatchEvent(new Event(Main.IsLoadedClass));
	}
}

export default Main;
